import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { Button, List, ListItem, ListDivider, Sheet, Snackbar, Typography, Chip, Divider, LinearProgress } from '@mui/joy';
import { DialogContent, DialogTitle, Modal, ModalClose, ModalDialog, ModalOverflow } from '@mui/joy';
import { Report } from '@mui/icons-material';
import { useAuth } from '../../context/authContext';

import LoadingSpinnerComponent from '../LoadingSpinnerComponent';
import PlanManagementComponent from './PlanManagementComponent';

import '../../styles/reviews.scss';
const networking = require('../../Networking/API');
const helper = require('../../Helpers/helpers');

function ClientPlanManagementComponent({ subscription, onSubscriptionUpdate }: any) {
    const location = useLocation();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showPlanChooseModal, setShowPlanChooseModal] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(subscription);
    const [teamMembersCount, setTeamMembersCount] = useState(0);
    const [appsCount, setAppsCount] = useState(0);

    useEffect(() => {
        if (currentSubscription.plan !== 'U') {
            // Fetch team members count
            setTeamMembersCount(!currentSubscription.user_list ? 1 : currentSubscription.user_list.length);

            // Fetch apps count
            networking.appsFor(currentSubscription.id, token).then((apps: any) => {
                setAppsCount(apps ? apps.length : 0);
            }).catch(() => {
                setAppsCount(0);
            });
        }
    }, [currentSubscription, token]);

    const labelForPlan = (plan: string) => {
        switch (plan) {
            case 'U':
                return 'Premium'

            case 'AA':
                return 'Starter'

            case 'AAA':
                return 'Growth'
        }
    }

    const updateSubscription = (event: any) => {
        event.preventDefault();
        setLoading(true);
        setShowError(false);

        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());
        formJson.id = subscription.id;
        formJson.plan = subscription.plan;
        formJson.profileimage = currentSubscription.profileimage;

        networking.updateSubscription(formJson).then((sub: any) => {
            getNewData()
        }).catch((error: Error) => {
            setShowError(true);
            setError(error.message);
            setLoading(false);
        });
    }

    const getNewData = () => {
        setLoading(true);

        networking.authenticateSubscription(token).then((sub: any) => {
            setLoading(false);
            setCurrentSubscription(sub);
            setShowSuccess(true);

            onSubscriptionUpdate(sub)

        }).catch((error: Error) => {
            setShowError(true);
            setError(error.message);
            setLoading(false);
        });
    }

    const openCustomerPortal = (event: any) => {
        setLoading(true);

        networking.customerPortal(subscription, location.pathname).then((sub: any) => {
            setLoading(false);

            if (sub.portal_url) {
                window.open(sub.portal_url, 'noopener,noreferer');
            }

        }).catch((error: Error) => {
            setError(error.message);
            setLoading(false);
        });
    }

    return (
        <>
            {/* Plan Modal */}
            <Modal
                open={showPlanChooseModal}
                onClose={() => setShowPlanChooseModal(false)}>
                <ModalOverflow>
                    <ModalDialog
                        color="neutral"
                        size="sm"
                        variant="plain">
                        <ModalClose />
                        <DialogTitle>
                            Plans & Billing
                        </DialogTitle>
                        <DialogContent>
                            Manage your billing and plan settings.
                        </DialogContent>
                        <Divider inset="none" />
                        <div className='pt-4'>
                            <PlanManagementComponent
                                subscription={subscription}
                                onSubscriptionUpdate={updateSubscription}
                            />
                        </div>
                    </ModalDialog>
                </ModalOverflow>
            </Modal>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            {showSuccess &&
                <Snackbar
                    variant="soft"
                    color="success"
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowSuccess(false)}
                            size="sm"
                            variant="soft"
                            color="success"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    Profile updated! 🚀
                </Snackbar>
            }
            {loading ?
                <LoadingSpinnerComponent />
                :
                <div className='row gx-2'>
                    <Sheet
                        variant='plain'
                        sx={{
                            p: 0,
                            borderRadius: 8
                        }}>
                        <div className='p-2'>
                            <div className='row gap-5 mb-3'
                                style={{
                                    borderRadius: 8,
                                }}>
                                <div className='col mb-2'>
                                    {subscription.status === 'trial_expired' &&
                                        <div className='row mb-4'>
                                            <div className='col-auto my-auto'>
                                                <Chip color='danger'
                                                    size='sm'>
                                                    Trial expired
                                                </Chip>
                                            </div>
                                            <div className='col-auto my-auto'>
                                                <Typography
                                                    level="title-sm"
                                                    className='mt-2' >
                                                    Your trial period has ended {Math.floor((new Date().getTime() - new Date(subscription.trial_ends_at).getTime()) / (1000 * 60 * 60 * 24))} days ago.
                                                </Typography>
                                            </div>
                                        </div>
                                    }
                                    {subscription.status === 'expired' &&
                                        <div className='row mb-4'>
                                            <div className='col-auto my-auto'>
                                                <Chip color='danger'
                                                    size='sm'>
                                                    Expired
                                                </Chip>
                                            </div>
                                            <div className='col-auto my-auto'>
                                                <Typography
                                                    level="title-sm"
                                                    className='mt-2' >
                                                    Your subscription expired {Math.floor((new Date().getTime() - new Date(subscription.expirationdate).getTime()) / (1000 * 60 * 60 * 24))} days ago.
                                                </Typography>
                                            </div>
                                        </div>
                                    }
                                    <div className='row'>
                                        <div className='col-auto'>
                                            <div
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 8,
                                                    backgroundColor: '#f5f5f5',
                                                    border: '1px solid #e0e0e0'
                                                }}>
                                                <Typography
                                                    level="h4"
                                                    className='m-auto'
                                                    sx={{ lineHeight: '48px', textAlign: 'center', color: '#9e9e9e' }}>
                                                    {labelForPlan(currentSubscription?.plan)!.charAt(0)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className='col'>
                                            <Typography
                                                level="title-md"
                                                className='mb-1'>
                                                You're currently on the {labelForPlan(currentSubscription.plan)} plan.
                                                <span
                                                    style={{ marginLeft: 8 }}>
                                                    <Chip
                                                        size="sm"
                                                        variant="soft"
                                                        color="primary">
                                                        {currentSubscription.billing_cycle}
                                                    </Chip>
                                                </span>
                                            </Typography>
                                            <Typography
                                                level="body-xs">
                                                {currentSubscription.status === 'trial' ? 'Your trial ends on : ' : "Renews on : "}
                                                <span>
                                                    <Typography
                                                        level="body-xs"
                                                        fontWeight="lg">
                                                        {helper.formatFriendlyDate(currentSubscription.expirationdate, navigator.language)}
                                                    </Typography>
                                                </span>
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                                <div className='col-auto my-auto'>
                                    {subscription.plan !== 'U' &&
                                        <Button
                                            size='sm'
                                            variant='solid'
                                            onClick={() => setShowPlanChooseModal(true)}>
                                            Change plan
                                        </Button>
                                    }
                                </div>
                            </div>
                            <Divider />
                            {currentSubscription.plan !== 'U' &&
                                <>
                                    <Typography
                                        sx={{ marginTop: 2 }}
                                        level="body-xs">
                                        Usage
                                    </Typography>

                                    <>
                                        <div className='row mb-2 ps-2 pt-2 pb-4 gap-2'>
                                            <div className='col-5'
                                                style={{
                                                    background: '#f5f5f5',
                                                    borderRadius: 8,
                                                    padding: 8
                                                }}>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        marginBottom: 2
                                                    }}>
                                                    Seats
                                                </Typography>
                                                <Typography
                                                    level="body-md"
                                                    fontWeight="lg"
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                    {teamMembersCount} / {currentSubscription.plan === 'AA' ? '3' : '5'}
                                                </Typography>
                                                <LinearProgress
                                                    determinate
                                                    value={Math.min((teamMembersCount / (currentSubscription.plan === 'AA' ? 3 : 5)) * 100, 100)}
                                                    color={teamMembersCount >= (currentSubscription.plan === 'AA' ? 3 : 5) ? 'warning' : 'primary'}
                                                    size="sm"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            </div>
                                            <div className='col-5'
                                                style={{
                                                    background: '#f5f5f5',
                                                    borderRadius: 8,
                                                    padding: 8
                                                }}>
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        marginBottom: 2
                                                    }}>
                                                    Apps
                                                </Typography>
                                                <Typography
                                                    level="body-md"
                                                    fontWeight="lg"
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                    {appsCount} / {currentSubscription.plan === 'AA' ? '3' : '10'}
                                                </Typography>
                                                <LinearProgress
                                                    determinate
                                                    value={Math.min((appsCount / (currentSubscription.plan === 'AA' ? 3 : 10)) * 100, 100)}
                                                    color={appsCount >= (currentSubscription.plan === 'AA' ? 3 : 10) ? 'warning' : 'primary'}
                                                    size="sm"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                    {currentSubscription.status !== 'trial' &&
                                        <>
                                            <Divider />
                                            <div className='mt-3 gap-2 d-flex'>
                                                <Button
                                                    size='sm'
                                                    variant='plain'
                                                    onClick={(e) => openCustomerPortal(e)}>
                                                    Manage billing
                                                </Button>
                                            </div>
                                        </>
                                    }
                                </>
                            }

                        </div>
                    </Sheet >
                    {
                        currentSubscription.plan === 'U' &&
                        <Sheet
                            variant='outlined'
                            sx={{
                                p: 1,
                                borderRadius: 8,
                                mt: 2
                            }}>
                            <div className='p-3'>
                                <Typography
                                    level="title-sm">
                                    Premium features included:
                                </Typography>
                                <ListDivider
                                    sx={{
                                        mt: 1,
                                        mb: 1
                                    }} />
                                <List aria-labelledby="decorated-list-demo">
                                    <ListItem>
                                        <Chip color='primary'>Proactive feedback</Chip>
                                    </ListItem>
                                </List>
                            </div>
                        </Sheet>
                    }
                </div >
            }
        </>
    );
}

export default ClientPlanManagementComponent;